// frontend/src/components/notes/MinimalNoteEditor.js
// GPT5 Specification: Minimal, fast, calm UX like iPhone Notes/Notion

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Divider,
  Fade,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  Code,
  FormatQuote,
  FormatListBulleted,
  FormatListNumbered,
  CheckBox,
  HorizontalRule,
  Image as ImageIcon,
  Mic,
  Stop,
  MoreVert,
  Delete,
  AutoAwesome,
} from '@mui/icons-material';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useRecording } from '../../contexts/SimpleRecordingContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagProvider';
import TitleSuggestionChip from '../TitleSuggestionChip';
import axios from '../../api/axiosConfig';

// Import task extensions normally for now
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Image from '@tiptap/extension-image';
import { titleService } from '../../services/titleService';
import { toMarkdown, fromMarkdown } from '../../services/markdownService';
import MarkdownPreview from './MarkdownPreview';

const MinimalNoteEditor = ({
  noteId = null,
  projectId = null,
  onSave = null,
  onCancel = () => {},
  onEditorFocus = () => {},
  onEditorBlur = () => {},
  initialTitle = '',
  initialContent = '',
  initialFormat = 'markdown',
  originalFormat = null,
  note = null // NEW: Full note object for aiTitle and other metadata
}) => {
  const { currentUser } = useAuth();
  const {
    isRecording,
    currentTranscript,
    toggleRecording
  } = useRecording();
  const featureFlags = useFeatureFlags();
  const hideVoiceUI = featureFlags.get('notes.composer.voiceOff');
  const aiTitleNotesEnabled = featureFlags.get('ai.title.auto.notes.v1');
  const stickyActionsEnabled = featureFlags.get('notes.stickyActions.v1');
  const markdownModeEnabled = featureFlags.get('notes.markdown.v1');

  // Normalize format on load: contentType > format > initialFormat (Phase 2 nit)
  const normalizedFormat = note?.contentType || note?.format || initialFormat;

  // Core state
  const [title, setTitle] = useState(initialTitle); // OLD: Used when stickyActions disabled
  const [autoTitle, setAutoTitle] = useState(initialTitle || ''); // NEW: Auto-extracted title
  const [content, setContent] = useState(initialContent);
  const [format, setFormat] = useState(normalizedFormat);
  const [titleLocked, setTitleLocked] = useState(false);
  
  // Status state
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  
  // UI state
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuAnchor, setSlashMenuAnchor] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [projectMenuAnchor, setProjectMenuAnchor] = useState(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [projects, setProjects] = useState([]);
  
  // Mode preference (simplified - hidden by default)
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('noteEditor.mode') || 'rich';
  });

  // Markdown mode state (NEW for Tier 1)
  const [editorMode, setEditorMode] = useState('rich'); // 'rich' | 'markdown'
  const [markdownContent, setMarkdownContent] = useState('');

  // Refs
  const autosaveTimeoutRef = useRef(null);
  const autosaveDisabledRef = useRef(false);
  const lastSaveTimeRef = useRef(Date.now());
  const saveStatusTimeoutRef = useRef(null);
  const titleInputRef = useRef(null);
  const lastTranscriptLengthRef = useRef(0);
  const fileInputRef = useRef(null); // NEW: For image upload

  // Computed title: Use aiTitle or fallback to first line
  const computedTitle = useMemo(() => {
    // If we have an AI-generated title, use it
    if (note?.aiTitle) return note.aiTitle;

    // Otherwise, extract first line from content
    const plainText = note?.markdown ||
                     note?.content?.replace(/<[^>]*>/g, '') ||
                     note?.text?.replace(/<[^>]*>/g, '') ||
                     note?.transcript || '';
    const firstLine = plainText.split('\n')[0]?.trim();

    return firstLine || 'Untitled';
  }, [note?.aiTitle, note?.markdown, note?.content, note?.text, note?.transcript]);
  
  // Performance: Initialize editor with essential extensions
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        link: false, // Disable to avoid duplicate with our custom Link config
        underline: false // Disable to avoid duplicate with our custom Underline config
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      TextStyle,
      Color.configure({
        types: ['textStyle']
      }),
      Placeholder.configure({
        placeholder: 'Start writing or type / for commands...'
      }),
      TaskList,
      TaskItem.configure({
        nested: true
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'note-image',
          style: 'max-width: 100%; height: auto; display: block; margin: 1rem 0; border-radius: 4px;'
        }
      })
    ],
    content: mode === 'rich' ? initialContent : '',
    onUpdate: ({ editor }) => {
      if (mode === 'rich') {
        const htmlContent = editor.getHTML();
        setContent(htmlContent);
        setFormat('richtext');
        // TEMPORARILY DISABLED: debouncedAutosave(title, htmlContent, 'richtext');
      }
    },
    onFocus: useCallback(() => {
      onEditorFocus();
    }, [onEditorFocus]),
    onBlur: useCallback(() => {
      onEditorBlur();
      // Save on blur for reliability
      const titleToSave = stickyActionsEnabled ? autoTitle : title;
      if (content.trim() || titleToSave.trim()) {
        handleAutosave(titleToSave, content, format);
      }
    }, [onEditorBlur, stickyActionsEnabled, autoTitle, title, content, format]),
    editorProps: {
      attributes: {
        class: 'minimal-editor-content'
      },
      handleKeyDown: (view, event) => {
        // Only intercept slash commands - leave all other keys unhandled
        if (event.key === '/') {
          setSlashMenuAnchor({ top: 0, left: 0 }); // Will be positioned by portal
          setShowSlashMenu(true);
          return true; // Prevent normal slash typing to show menu
        }
        // Return false to let Tiptap handle all other keys (arrows, typing, etc.)
        return false;
      },
      // NEW: Drag-drop image handler (Tier 2)
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer?.files?.length) {
          const files = Array.from(event.dataTransfer.files);
          const imageFiles = files.filter(file => file.type.startsWith('image/'));

          if (imageFiles.length > 0) {
            event.preventDefault();
            imageFiles.forEach(async (file) => {
              try {
                setSaveStatus('saving');
                const token = await currentUser.getIdToken();

                const formData = new FormData();
                formData.append('image', file);

                const response = await axios.post('/api/files/images', formData, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                  }
                });

                const { url } = response.data;

                // Insert image at drop position
                const { schema } = view.state;
                const node = schema.nodes.image.create({ src: url, alt: file.name });
                const transaction = view.state.tr.insert(
                  view.state.selection.from,
                  node
                );
                view.dispatch(transaction);

                setSaveStatus('saved');
              } catch (err) {
                console.error('Image upload failed:', err);
                setError('Failed to upload image');
                setSaveStatus('idle');
              }
            });
            return true; // Prevent default paste
          }
        }
        return false;
      }
    }
  });

  // Auto-focus title for new notes
  useEffect(() => {
    if (!noteId && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [noteId]);

  // Persist mode preference
  useEffect(() => {
    localStorage.setItem('noteEditor.mode', mode);
  }, [mode]);

  useEffect(() => {
    return () => {
      autosaveDisabledRef.current = true;
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }
    };
  }, []);

  // Handle save status dot fade
  useEffect(() => {
    if (saveStatus === 'saved') {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
      saveStatusTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 1500);
    }
    return () => {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
    };
  }, [saveStatus]);

  // Enterprise auto-title generation with user edit protection
  useEffect(() => {
    if (!editor || !content || content.trim() === '') return;

    // Only auto-generate for new notes or unlocked notes
    const shouldGenerate = !noteId || noteId === 'new' || !titleService.isUserLocked(noteId);

    if (shouldGenerate && (!title || title.trim() === '')) {
      const textContent = editor.getText();

      if (textContent.trim().length > 0) {
        const result = titleService.generateTitle(
          noteId || 'new',
          textContent,
          title,
          {
            respectUserEdits: true,
            maxLength: 50,
            fallbackTitle: 'Untitled'
          }
        );

        if (result.generated && result.title !== title) {
          setTitle(result.title);
        }
      }
    }
  }, [editor, content, title, noteId]);

  // NEW: Auto-extract title from first line (sticky actions v1)
  useEffect(() => {
    if (!stickyActionsEnabled) return; // Only active when feature enabled
    if (!editor || titleLocked) return; // Skip if user manually edited

    const text = editor.getText().trim();
    const firstLine = text.split('\n')[0]?.trim() || '';

    if (firstLine) {
      const extractedTitle = firstLine.length > 80
        ? firstLine.slice(0, 80) + '…'
        : firstLine;
      setAutoTitle(extractedTitle);
    } else {
      setAutoTitle('Untitled Note');
    }
  }, [editor?.state.doc, titleLocked, stickyActionsEnabled]);

  // Reset titleLocked when creating new note
  useEffect(() => {
    if (!noteId || noteId === 'new') {
      setTitleLocked(false);
    }
  }, [noteId]);

  // Insert live transcript into editor as it arrives
  useEffect(() => {
    if (currentTranscript && editor) {
      const newText = currentTranscript.substring(lastTranscriptLengthRef.current);
      if (newText) {
        editor.chain().focus().insertContent(newText).run();
        lastTranscriptLengthRef.current = currentTranscript.length;
      }
    }
  }, [currentTranscript, editor]);

  // Debounced autosave with reasonable timing
  const debouncedAutosave = useCallback((titleText, contentText, formatType) => {
    if (autosaveDisabledRef.current) return;

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    // 10 second autosave delay for less disruption
    const timeSinceLastSave = Date.now() - lastSaveTimeRef.current;
    const delay = Math.max(10000, 10000 - timeSinceLastSave); // 10s debounce, 10s minimum between saves

    autosaveTimeoutRef.current = setTimeout(() => {
      if (autosaveDisabledRef.current) return;
      handleAutosave(titleText, contentText, formatType);
    }, delay);
  }, []);

  // Optimistic autosave with minimal UI feedback
  const handleAutosave = async (titleText, contentText, formatType) => {
    if (autosaveDisabledRef.current) return;
    if (!titleText.trim() && !contentText.trim()) return;

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
    
    try {
      setSaveStatus('saving');
      // Use requestIdleCallback for contentHtml generation if available
      const generateContent = () => {
        const payload = {
          title: titleText.trim(),
          format: formatType,
          content: formatType === 'richtext' ? '' : contentText,
          contentHtml: formatType === 'richtext' ? contentText : '',
          projectId: projectId || null
        };
        return payload;
      };

      const payload = typeof requestIdleCallback !== 'undefined' 
        ? await new Promise(resolve => requestIdleCallback(() => resolve(generateContent())))
        : generateContent();

      if (noteId && noteId !== 'new') {
        // Update existing note
        const response = await axios.patch(`/api/notes/${noteId}`, payload);

        if (onSave && response.data?.note) {
          onSave(response.data.note);
        }
      } else {
        // Create new note
        const response = await axios.post('/api/notes', payload);

        // Update editor with real ID after successful creation
        if (onSave && response.data?.note) {
          onSave(response.data.note);
        }
      }
      
      lastSaveTimeRef.current = Date.now();
      setSaveStatus('saved');
      setError('');
    } catch (err) {
      console.error('Autosave failed:', err);
      setError(`Save failed: ${err.response?.headers?.['x-request-id'] || 'Unknown error'}`);
      setSaveStatus('idle');
    }
  };

  // Manual save handler
  const handleManualSave = useCallback(() => {
    const titleToSave = stickyActionsEnabled ? autoTitle : title;
    handleAutosave(titleToSave, content, format);
  }, [autoTitle, title, content, format, stickyActionsEnabled, handleAutosave]);

  // NEW: Autosave on 2s idle (sticky actions v1)
  useEffect(() => {
    if (!stickyActionsEnabled) return; // Only active when feature enabled
    if (!content.trim()) return;

    const timeout = setTimeout(() => {
      // Use existing debounced autosave for rate limiting
      debouncedAutosave(autoTitle, content, format);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [autoTitle, content, format, stickyActionsEnabled, debouncedAutosave]);

  // Ctrl+S/Cmd+S shortcut for manual save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        const titleToSave = stickyActionsEnabled ? autoTitle : title;
        handleAutosave(titleToSave, content, format);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [autoTitle, title, content, format, stickyActionsEnabled]);

  // Handle voice recording toggle
  const handleVoiceRecord = useCallback(async () => {
    await toggleRecording();
  }, [toggleRecording]);

  // Open project selection menu and fetch projects if needed
  const handleProjectMenuOpen = async (event) => {
    setProjectMenuAnchor(event.currentTarget);
    if (projects.length === 0) {
      try {
        const response = await axios.get('/api/projects');
        setProjects(response.data.projects || []);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    }
  };

  const handleProjectMenuClose = () => {
    setProjectMenuAnchor(null);
  };

  const handleProjectSelect = async (projId) => {
    if (!noteId) return;
    try {
      await axios.patch(`/api/notes/${noteId}/project`, { projectId: projId });
      handleProjectMenuClose();
      // Show success feedback instead of page reload
      setAlert({ type: 'success', message: 'Note added to project successfully!' });
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      console.error('Failed to update project:', err);
    }
  };

  const handleCreateProject = async () => {
    const name = window.prompt('Project name');
    if (!name) return;
    try {
      const response = await axios.post('/api/projects', { name });
      const newProject = response.data.project;
      setProjects(prev => [...prev, newProject]);
      await handleProjectSelect(newProject._id);
    } catch (err) {
      console.error('Failed to create project:', err);
    }
  };

  // Slash command handlers
  const handleSlashCommand = (command) => {
    if (!editor) return;
    
    // Remove the slash character first
    editor.chain().focus().deleteRange({ from: editor.state.selection.from - 1, to: editor.state.selection.from }).run();
    
    switch (command) {
      case 'h1':
        editor.chain().focus().toggleHeading({ level: 1 }).run();
        break;
      case 'h2':
        editor.chain().focus().toggleHeading({ level: 2 }).run();
        break;
      case 'h3':
        editor.chain().focus().toggleHeading({ level: 3 }).run();
        break;
      case 'bullet':
        editor.chain().focus().toggleBulletList().run();
        break;
      case 'number':
        editor.chain().focus().toggleOrderedList().run();
        break;
      case 'check':
        editor.chain().focus().toggleTaskList().run();
        break;
      case 'quote':
        editor.chain().focus().toggleBlockquote().run();
        break;
      case 'code':
        editor.chain().focus().toggleCodeBlock().run();
        break;
      case 'divider':
        editor.chain().focus().setHorizontalRule().run();
        break;
      case 'image':
        handleImageUpload();
        break;
      default:
        break;
    }
    
    setShowSlashMenu(false);
  };

  // Image upload
  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          setSaveStatus('saving');
          const token = await currentUser.getIdToken();

          const formData = new FormData();
          formData.append('image', file);

          const response = await axios.post('/api/files/images', formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });

          const { url } = response.data;
          editor.chain().focus().setImage({ src: url, alt: file.name }).run();
          setSaveStatus('saved');
        } catch (err) {
          console.error('Image upload failed:', err);
          setError('Failed to upload image');
        }
      }
    };
    input.click();
  };

  // NEW: File select handler for "Insert Image" button (Tier 2)
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setSaveStatus('saving');
      const token = await currentUser.getIdToken();

      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post('/api/files/images', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const { url } = response.data;
      if (editor) {
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      }
      setSaveStatus('saved');

      // Reset input so same file can be selected again
      event.target.value = '';
    } catch (err) {
      console.error('Image upload failed:', err);
      setError('Failed to upload image');
      setSaveStatus('idle');
    }
  };

  // Phase 3: Mode change handler for Rich Text ↔ Markdown with persistence
  const handleModeChange = async (event, newMode) => {
    if (!newMode || !editor) return; // Prevent deselecting both
    if (newMode === editorMode) return; // Already in this mode

    try {
      setSaveStatus('saving');

      if (newMode === 'markdown') {
        // Convert TipTap HTML to Markdown
        const markdown = toMarkdown(editor);
        setMarkdownContent(markdown);
        setFormat('markdown');

        // Persist to backend if note exists
        if (noteId && noteId !== 'new') {
          const token = await currentUser.getIdToken();
          await axios.patch(
            `/api/notes/${noteId}`,
            {
              contentType: 'markdown',
              markdown: markdown,
              content: '' // Clear HTML content
            },
            { headers: { Authorization: `Bearer ${token}` }}
          );
        }
      } else {
        // Convert Markdown to HTML for TipTap
        const html = fromMarkdown(markdownContent);
        editor.commands.setContent(html);
        setContent(html);
        setFormat('richtext');

        // Persist to backend if note exists
        if (noteId && noteId !== 'new') {
          const token = await currentUser.getIdToken();
          await axios.patch(
            `/api/notes/${noteId}`,
            {
              contentType: 'richtext',
              content: html,
              markdown: '' // Clear markdown content
            },
            { headers: { Authorization: `Bearer ${token}` }}
          );
        }
      }

      setEditorMode(newMode);
      setSaveStatus('saved');

      // Show success feedback
      setAlert({ type: 'success', message: `Switched to ${newMode === 'markdown' ? 'Markdown' : 'Rich Text'} mode` });
      setTimeout(() => setAlert(null), 2000);

    } catch (error) {
      console.error('Mode switch failed:', error);
      setError(`Failed to switch mode: ${error.message}`);
      setSaveStatus('idle');
    }
  };

  // Title change handler with enterprise protection
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);

    // Lock title generation for this note if user manually edits
    if (noteId && noteId !== 'new') {
      titleService.lockTitle(noteId, 'manual_edit');
    }

    setTitleLocked(true); // Lock suggestions once user types
    // TEMPORARILY DISABLED: debouncedAutosave(newTitle, content, format);
  };

  // Handle title suggestion apply
  const handleTitleSuggestionApply = (suggestedTitle) => {
    setTitle(suggestedTitle);
    setTitleLocked(true); // Lock after applying suggestion
    // TEMPORARILY DISABLED: debouncedAutosave(suggestedTitle, content, format);
  };

  // Handle title suggestion dismiss
  const handleTitleSuggestionDismiss = () => {
    // Suggestions will be hidden by the component itself
  };

  // Markdown content handler
  const handleMarkdownChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    setFormat('markdown');
    // TEMPORARILY DISABLED: debouncedAutosave(title, newContent, 'markdown');
  };

  // Actions menu handlers
  const handleActionsMenuOpen = (event) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  // Delete note handler
  const handleDeleteNote = async () => {
    if (!noteId) return;
    
    if (!window.confirm(`Delete "${title || 'Untitled'}"?`)) return;

    try {
      autosaveDisabledRef.current = true;
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }

      await axios.delete(`/api/notes/${noteId}`);
      handleActionsMenuClose();
      onCancel(); // Close the editor and go back to the list
    } catch (error) {
      console.error('Failed to delete note:', error);
      setError('Failed to delete note');
    }
  };

  // Generate AI title (Phase 2: Use new endpoint)
  const handleGenerateTitle = async () => {
    if (!noteId || noteId === 'new') {
      setError('Save the note first before generating AI title');
      return;
    }

    if (!content || content.trim().length < 20) {
      setError('Note content too short for AI title generation (minimum 20 characters)');
      return;
    }

    setSaveStatus('saving');
    try {
      const response = await axios.post(
        `/api/notes/${noteId}/ai-title`,
        {}
      );

      // Update note state with new AI title - use full note from backend response
      if (onSave && response.data.note) {
        onSave(response.data.note);
        setAlert({ type: 'success', message: 'AI title generated successfully!' });
        setTimeout(() => setAlert(null), 3000);
      }

      setSaveStatus('saved');
      handleActionsMenuClose();
    } catch (error) {
      console.error('Failed to generate title:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to generate AI title';
      setError(errorMsg);
      setSaveStatus('idle');
    }
  };

  // Save status indicator component
  const SaveStatusDot = () => {
    if (saveStatus === 'idle') return null;
    
    return (
      <Fade in={saveStatus !== 'idle'}>
        <Box sx={{ 
          position: 'absolute', 
          right: 8, 
          top: '50%', 
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span className="save-indicator">
            <span className={`dot ${saveStatus === 'saving' ? 'saving' : ''}`}></span>
            {saveStatus === 'saving' ? 'Saving…' : 'Saved'}
          </span>
        </Box>
      </Fade>
    );
  };

  // TODO: Add floating toolbar component when BubbleMenu is available

  // Render based on mode
  if (mode === 'markdown') {
    return (
      <Box 
        sx={{ 
          width: '100%',
          minHeight: '100vh',
          bgcolor: 'background.default',
          py: 4,
          px: 2
        }}
      >
        <Box 
          sx={{ 
            maxWidth: '960px',
            mx: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            height: 'calc(100vh - 120px)', // Account for padding
            display: 'flex', 
            flexDirection: 'column'
          }}
        >
          <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Title with status dot */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          {noteId && noteId !== 'new' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '22px',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  flex: 1
                }}
              >
                {computedTitle}
              </Typography>
              {note?.aiTitle && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.5,
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    borderRadius: 1,
                    fontSize: '11px',
                    fontWeight: 600
                  }}
                >
                  AI
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <TextField
                ref={titleInputRef}
                value={title}
                onChange={handleTitleChange}
                placeholder="Untitled"
                variant="standard"
                fullWidth
                name="note-title"
                onFocus={onEditorFocus}
                onBlur={onEditorBlur}
                InputProps={{
                  disableUnderline: true,
                  className: "editor-title",
                  sx: {
                    fontSize: '22px',
                    fontWeight: 600,
                    margin: '4px 0 12px',
                    border: 'none',
                    outline: 'none'
                  }
                }}
              />

              {/* AI Title Suggestion */}
              {aiTitleNotesEnabled && (
                <TitleSuggestionChip
                  content={content || (editor?.getText && editor.getText()) || ''}
                  contentType="note"
                  currentTitle={title}
                  onApply={handleTitleSuggestionApply}
                  onDismiss={handleTitleSuggestionDismiss}
                  isLocked={titleLocked}
                  sx={{ mb: 1 }}
                />
              )}
            </>
          )}

          <SaveStatusDot />
        </Box>

        {/* Always visible toolbar with microphone for Markdown */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 2, 
          pb: 1, 
          borderBottom: '1px solid #f0f0f0' 
        }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
            Markdown
          </Typography>
          
          {/* Microphone - hidden when flag is set */}
          {!hideVoiceUI && (
            <IconButton 
              size="small" 
              onClick={handleVoiceRecord}
              color={isRecording ? 'secondary' : 'default'}
              sx={{ 
                ml: 'auto',
                backgroundColor: isRecording ? 'secondary.light' : 'transparent',
                '&:hover': {
                  backgroundColor: isRecording ? 'secondary.main' : 'action.hover'
                }
              }}
            >
              {isRecording ? <Stop sx={{ fontSize: 18 }} /> : <Mic sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
          
        </Box>

        {/* Markdown textarea */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <TextField
            multiline
            fullWidth
            value={content}
            onChange={handleMarkdownChange}
            placeholder="Write in Markdown..."
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '16px',
                lineHeight: 1.6,
                fontFamily: 'monospace',
                border: 'none',
                outline: 'none'
              }
            }}
            sx={{ 
              height: '100%',
              '& .MuiInputBase-root': {
                height: '100%'
              },
              '& .MuiInputBase-input': {
                height: '100% !important',
                overflow: 'auto !important'
              }
            }}
          />
        </Box>

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Success alert display */}
        {alert && (
          <Alert severity={alert.type} sx={{ mt: 2 }} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
        px: 2
      }}
    >
      <Box 
        sx={{ 
          maxWidth: '960px',
          mx: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        {/* NEW: Sticky Header (sticky actions v1) */}
        {stickyActionsEnabled && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            {/* Left: Title (display-only for existing notes, editable for new) */}
            {noteId && noteId !== 'new' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: { xs: '1 1 100%', sm: 1 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flex: 1
                  }}
                >
                  {computedTitle}
                </Typography>
                {note?.aiTitle && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1,
                      py: 0.25,
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      borderRadius: 1,
                      fontSize: '10px',
                      fontWeight: 600
                    }}
                  >
                    AI
                  </Typography>
                )}
              </Box>
            ) : (
              <TextField
                value={autoTitle}
                onChange={(e) => {
                  setAutoTitle(e.target.value);
                  setTitleLocked(true); // Lock on manual edit
                }}
                variant="standard"
                placeholder="Untitled Note"
                sx={{
                  flex: { xs: '1 1 100%', sm: 1 },
                  fontWeight: 600,
                  fontSize: '18px',
                  '& input': { padding: 0 }
                }}
                InputProps={{ disableUnderline: true }}
              />
            )}

            {/* Center Left: Mode Toggle (NEW - Tier 1) */}
            {markdownModeEnabled && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Mode:
                </Typography>
                <ToggleButtonGroup
                  value={editorMode}
                  exclusive
                  onChange={handleModeChange}
                  size="small"
                  sx={{ height: '28px' }}
                >
                  <ToggleButton value="rich">Rich Text</ToggleButton>
                  <ToggleButton value="markdown">Markdown</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Center: Save indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {saveStatus === 'saving' && (
                <>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Saving...
                  </Typography>
                </>
              )}
              {saveStatus === 'saved' && (
                <Typography variant="caption" color="success.main">
                  ✓ Saved
                </Typography>
              )}
            </Box>

            {/* Right: Quick actions */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Insert Image (NEW - Tier 2) */}
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                title="Insert Image"
              >
                <ImageIcon sx={{ fontSize: 18 }} />
              </IconButton>

              {/* Manual Save Button */}
              <Button
                size="small"
                variant="outlined"
                onClick={handleManualSave}
                disabled={saveStatus === 'saving'}
                sx={{ minWidth: '60px' }}
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save'}
              </Button>

              {/* AI Title Generation */}
              {aiTitleNotesEnabled && (
                <IconButton
                  size="small"
                  onClick={handleGenerateTitle}
                  title="Generate AI Title"
                  disabled={saveStatus === 'saving'}
                >
                  <AutoAwesome sx={{ fontSize: 18 }} />
                </IconButton>
              )}

              {/* Delete Note */}
              {noteId && noteId !== 'new' && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDeleteNote}
                  title="Delete Note"
                >
                  <Delete sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {/* Hidden file input for image upload (NEW - Tier 2) */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Box>
        )}

        <Box sx={{ p: 4 }}>
        {/* OLD: Title with status dot (only shown when sticky actions disabled) */}
        {!stickyActionsEnabled && (
          <Box sx={{ position: 'relative', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            {noteId && noteId !== 'new' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    fontSize: '24px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flex: 1
                  }}
                >
                  {computedTitle}
                </Typography>
                {note?.aiTitle && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1,
                      py: 0.5,
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      borderRadius: 1,
                      fontSize: '11px',
                      fontWeight: 600
                    }}
                  >
                    AI
                  </Typography>
                )}
              </Box>
            ) : (
              <TextField
                ref={titleInputRef}
                value={title}
                onChange={handleTitleChange}
                placeholder="No title"
                variant="standard"
                sx={{ flex: 1 }}
                name="note-title"
                onFocus={onEditorFocus}
                onBlur={onEditorBlur}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '24px',
                    fontWeight: 500,
                    marginBottom: '12px',
                    border: 'none',
                    outline: 'none'
                  }
                }}
              />
            )}
            <SaveStatusDot />
          </Box>
        )}

      {/* Always visible toolbar with microphone */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 2, 
        pb: 1, 
        borderBottom: '1px solid #f0f0f0' 
      }}>
        {/* Format buttons */}
        <IconButton size="small" onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '11px' }}>H1</Typography>
        </IconButton>
        <IconButton size="small" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '11px' }}>H2</Typography>
        </IconButton>
        <IconButton size="small" onClick={() => editor?.chain().focus().toggleBold().run()}>
          <FormatBold sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton size="small" onClick={() => editor?.chain().focus().toggleItalic().run()}>
          <FormatItalic sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton size="small" onClick={() => editor?.chain().focus().toggleBulletList().run()}>
          <FormatListBulleted sx={{ fontSize: 18 }} />
        </IconButton>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {noteId && (
            <>
              <Button size="small" variant="outlined" onClick={handleProjectMenuOpen}>
                Add to Project
              </Button>
              <Menu
                anchorEl={projectMenuAnchor}
                open={Boolean(projectMenuAnchor)}
                onClose={handleProjectMenuClose}
              >
                <MenuItem onClick={() => handleProjectSelect(null)}>None</MenuItem>
                {projects.map(project => (
                  <MenuItem key={project._id} onClick={() => handleProjectSelect(project._id)}>
                    {project.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={handleCreateProject}>Create Project</MenuItem>
              </Menu>
            </>
          )}
          
          {/* Actions Menu (Delete, AI features) - only show for existing notes */}
          {noteId && (
            <>
              <IconButton 
                size="small" 
                onClick={handleActionsMenuOpen}
                sx={{ ml: 1 }}
              >
                <MoreVert sx={{ fontSize: 18 }} />
              </IconButton>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={handleActionsMenuClose}
              >
                <MenuItem onClick={handleGenerateTitle}>
                  <AutoAwesome sx={{ mr: 1, fontSize: 18 }} />
                  Generate AI Title
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleDeleteNote} sx={{ color: 'error.main' }}>
                  <Delete sx={{ mr: 1, fontSize: 18 }} />
                  Delete Note
                </MenuItem>
              </Menu>
            </>
          )}
          
          {!hideVoiceUI && (
            <IconButton
              size="small"
              onClick={handleVoiceRecord}
              color={isRecording ? 'secondary' : 'default'}
              sx={{
                backgroundColor: isRecording ? 'secondary.light' : 'transparent',
                '&:hover': {
                  backgroundColor: isRecording ? 'secondary.main' : 'action.hover'
                }
              }}
            >
              {isRecording ? <Stop sx={{ fontSize: 18 }} /> : <Mic sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </Box>

      </Box>

      {/* Editor body - conditionally render Rich Text or Markdown mode */}
      <Box sx={{ flex: 1, position: 'relative', height: '100%' }}>
        {editorMode === 'rich' ? (
          <EditorContent
            editor={editor}
            style={{ height: '100%', outline: 'none' }}
          />
        ) : (
          <MarkdownPreview
            content={markdownContent}
            onChange={setMarkdownContent}
          />
        )}
      </Box>

      {/* Slash menu */}
      <Menu
        open={showSlashMenu}
        anchorEl={slashMenuAnchor}
        onClose={() => setShowSlashMenu(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuItem onClick={() => handleSlashCommand('h1')}>
          <Typography variant="body2">Heading 1</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSlashCommand('h2')}>
          <Typography variant="body2">Heading 2</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSlashCommand('h3')}>
          <Typography variant="body2">Heading 3</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleSlashCommand('bullet')}>
          <FormatListBulleted sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">Bullet List</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSlashCommand('number')}>
          <FormatListNumbered sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">Numbered List</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSlashCommand('check')}>
          <CheckBox sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">Checklist</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleSlashCommand('quote')}>
          <FormatQuote sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">Quote</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSlashCommand('code')}>
          <Code sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">Code Block</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSlashCommand('divider')}>
          <HorizontalRule sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">Divider</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleSlashCommand('image')}>
          <ImageIcon sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">Image</Typography>
        </MenuItem>
      </Menu>

      {/* More menu */}
      <Menu
        open={showMoreMenu}
        anchorEl={moreMenuAnchor}
        onClose={() => setShowMoreMenu(false)}
      >
        <MenuItem onClick={() => { setMode('markdown'); setShowMoreMenu(false); }}>
          Switch to Markdown
        </MenuItem>
        <MenuItem onClick={() => { setMode('preview'); setShowMoreMenu(false); }}>
          Preview
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => editor.chain().focus().toggleUnderline().run()}>
          Underline
        </MenuItem>
        <MenuItem onClick={() => editor.chain().focus().toggleCode().run()}>
          Inline Code
        </MenuItem>
        <MenuItem onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          Align Left
        </MenuItem>
        <MenuItem onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          Align Center
        </MenuItem>
        <MenuItem onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          Align Right
        </MenuItem>
      </Menu>

      {/* OLD: Action buttons (only shown when sticky actions disabled) */}
      {!stickyActionsEnabled && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          pt: 2,
          borderTop: '1px solid #f0f0f0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {saveStatus === 'saving' && (
              <>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Saving...
                </Typography>
              </>
            )}
            {saveStatus === 'saved' && (
              <Typography variant="caption" color="success.main">
                ✓ Saved
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleAutosave(title, content, format);
                if (onSave) {
                  onSave({ title, content, format });
                }
              }}
              disabled={saveStatus === 'saving'}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Success alert display */}
        {alert && (
          <Alert severity={alert.type} sx={{ mt: 2 }} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}
        </Box>
      </Box>
    </Box>
  );
};

// Styles are now in index.css for better organization

export default MinimalNoteEditor;